import useAPI from "@toothfairy/shared-api/useApi";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import clsx from "clsx";
import dayjs from "dayjs";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import envConfig from "../../../../envConfig";
import Files from "../../../API/Files";
import { useTreeview } from "../../../Hooks/useTreeView";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { useHistory } from "../../../Router";
import Utility from "../../../Utility";
import styles from "../Styles/index.css";
import TreeIcon from "./TreeIcon";
import { Popable } from "react-native-popable";

const dataTypes = [
  "pdf",
  "md",
  "txt",
  "html",
  "java",
  "py",
  "yaml",
  "yml",
  "sql",
  "sh",
  "php",
  "js",
  "ts",
  "csharp",
  "rb",
  "tsx",
  "jsx",
];

const artifactExtensions = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "mp3",
  "m4a",
  "aac",
  "flac",
  "ogg",
  "mp4",
  "webm",
  "mov",
];

const CoreNodeView = ({
  droppable,
  name,
  description,
  currentTheme,
  editLeafParentAction = "EDIT_FOLDER",
  editLeafAction = "EDIT_DOCUMENT",
  deleteAction = "DELETE_NODE",
  isDrag,
  treemode,
}) => {
  const coreComponent = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <span
        style={{
          color: currentTheme?.theme.primary,
          position: "relative",
          marginRight: 5,
        }}
      >
        {!droppable ? (
          <TreeIcon icon={AppAssets.icons.file} />
        ) : (
          <TreeIcon icon={AppAssets.icons.folder} />
        )}
      </span>
      <span
        style={{
          color: currentTheme?.theme.font_color,
          display: "inline-block",
          maxWidth: "180px",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          verticalAlign: "bottom",
          fontWeight: isDrag ? "bold" : "normal",
        }}
      >
        {name}
      </span>
    </div>
  );
  return treemode == "CHATS" ? (
    <Popable
      numberOfLines={2}
      backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
      style={[
        {
          top: 30,
          zIndex: 20,
          maxWidth: 300,
        },
      ]}
      content={description}
      action="hover"
      position="bottom"
    >
      {coreComponent}
    </Popable>
  ) : (
    coreComponent
  );
};

function Node({ node, style, dragHandle }) {
  const { currentTheme, mode } = AppTheme.useTheme();
  const { t, i18n } = useTranslation();
  dayjs.locale(i18n.language);
  const [isMouseOver, setMouseOver] = useState(false);
  const [nodeImage, setNodeImage] = useState(null);
  const {
    showIconsOnSelection,
    onSelectionChanged,
    selectedNode,
    onEdit,
    onDeletion,
    setDialogActionOnTree,
    editLeafParentAction,
    editLeafAction,
    deleteAction,
    allowParentEdit,
    allowParentAction,
    onParentActionClick,
    fixedNodeIds,
    treemode,
    onToggleFavorite,
    isMultiDeleteMode,
    multiSelectedIds,
    onToggleNodeSelection,
    onDropFilesOnNode,
  } = useTreeview();
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const isFileDragEvent = (e) =>
    !!e?.dataTransfer &&
    Array.from(e.dataTransfer.types || []).includes("Files");
  const handleNodeDragOver = (e) => {
    if (!node?.data?.droppable || !isFileDragEvent(e)) return;
    e.preventDefault();
    if (!isFileDragOver) setIsFileDragOver(true);
  };
  const handleNodeDragLeave = (e) => {
    if (!node?.data?.droppable || !isFileDragEvent(e)) return;
    e.preventDefault();
    setIsFileDragOver(false);
  };
  const handleNodeDrop = (e) => {
    if (!isFileDragEvent(e)) return;
    setIsFileDragOver(false);
  };

  const getNodeImageUrl = async (path) => {
    const _url = await S3downloadUrlRequest({
      filename: `imported-image/${getActiveWorkspace()?.id}/${path}`,
      context: "pdf",
      workspaceid: getActiveWorkspace()?.id,
    });
    if (_url?.url) setNodeImage(_url?.url);
  };
  useEffect(() => {
    setMouseOver(false);
  }, [node?.data?.id]);
  const { getActiveWorkspace, isUserAIEngineer } = useWorkspaces();
  const history = useHistory();
  useEffect(() => {
    // Only drive the single-node "open/preview" behaviour when this is a
    // single selection. When the user multi-selects (Cmd/Ctrl/Shift+click) we
    // keep every node highlighted for a multi-drag instead of navigating.
    const selectedCount = node?.tree?.selectedIds?.size ?? 0;
    if (node?.state?.isSelected && selectedCount <= 1) {
      onSelectionChanged(node);
    }
  }, [node?.state?.isSelected, node?.tree?.selectedIds?.size]);
  useEffect(() => {
    if (node?.data?.icon) {
      getNodeImageUrl(node?.data?.icon);
    }
  }, [node?.data?.icon]);
  function getTreeIcon(treemode, node) {
    if (treemode === "CHATS") {
      if (node?.data?.is_agent_group) {
        return AppAssets.icons.users;
      } else if (node?.data?.is_agent) {
        if (node?.data?.is_agent_available == false) {
          return AppAssets.icons.accessDenied;
        } else if (node?.data?.icon) return null;
        else {
          return Utility.mapAgentTypeWithIcon(node?.data, AppAssets);
        }
      } else if (node?.data?.is_time_group) {
        return AppAssets.icons.days;
      } else if (node?.data?.is_recent && !node?.data?.droppable) {
        return AppAssets.icons.day;
      } else {
        return AppAssets.icons.folder;
      }
    } else {
      return AppAssets.icons.folder;
    }
  }
  function getTreeTooltip(treemode, node) {
    if (treemode === "CHATS") {
      if (node?.data?.is_agent_group) {
        return node?.data?.name || t("ui.agentGroup");
      } else if (node?.data?.is_agent) {
        if (node?.data?.is_agent_available == false) {
          return t("ui.noAgentType");
        } else {
          return Utility.mapAgentTypeWithLabel(node?.data);
        }
      } else if (node?.data?.is_time_group) {
        return node?.data?.name || t("ui.timePeriod");
      } else {
        return t("ui.folder");
      }
    } else {
      return t("ui.folder");
    }
  }
  function getTreeIconColor(treemode, node) {
    if (treemode === "CHATS") {
      if (node?.data?.is_agent_group) {
        return node?.data?.groupColor || currentTheme?.theme.primary;
      } else if (node?.data?.is_agent) {
        if (node?.data?.is_agent_available == false) {
          return currentTheme?.theme.error;
        } else if (node?.data?.icon) return null;
        else {
          if (mode == "dark")
            return node?.data?.darkColor || currentTheme?.theme.primary;
          else return node?.data?.color || currentTheme?.theme.primary;
        }
      } else {
        return currentTheme?.theme.primary;
      }
    } else {
      return currentTheme?.theme.primary;
    }
  }
  return (
    <div
      data-tooltip={
        !node?.data?.droppable && !node?.state?.isSelected && isMouseOver
          ? node?.data?.name
          : null
      }
      onMouseEnter={() => {
        setMouseOver(true);
      }}
      onMouseLeave={() => {
        setMouseOver(false);
      }}
      onDragOver={handleNodeDragOver}
      onDragLeave={handleNodeDragLeave}
      onDrop={handleNodeDrop}
      data-drop-folder-id={
        node?.data?.droppable ? node?.id : node?.data?.parent || undefined
      }
      data-tooltip-position="bottom"
      ref={dragHandle}
      style={{
        ...style,
        flexDirection: "row",
        alignItems: "center",
        marginTop: 8,
        display: "flex",
        justifyContent: "space-between",
        ...(isFileDragOver && node?.data?.droppable
          ? {
              backgroundColor: currentTheme?.theme?.inputBackgroundColor,
              border: `1px dashed ${currentTheme?.theme?.primary}`,
              borderRadius: 4,
            }
          : {}),
      }}
      className={clsx(styles.node, node.state)}
      onClick={() => {
        node.isInternal && node.toggle();
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {isMultiDeleteMode && !fixedNodeIds?.includes(node?.id) && (
          <TreeIcon
            icon={
              multiSelectedIds?.includes(node?.id)
                ? AppAssets.icons.check
                : AppAssets.icons.uncheck
            }
            color={
              multiSelectedIds?.includes(node?.id)
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.darkGrey || "#666666"
            }
            wrapperStyle={{
              marginRight: 4,
              marginBottom: 2,
              flexShrink: 0,
            }}
            onPress={(e) => {
              e?.stopPropagation?.();
              onToggleNodeSelection(node?.id);
            }}
          />
        )}
        {/* {treemode != "CHATS" && (
          <FolderArrow
            node={node}
            onPress={() => {
              node.isInternal && node.toggle();
            }}
          />
        )} */}

        <span
          style={{
            color: currentTheme?.theme.primary,
            position: "relative",
            marginRight: 5,
          }}
        >
          {!node?.data?.droppable ? (
            treemode == "CHATS" && node?.data?.is_recent ? (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={{
                  zIndex: 20,
                  width: 200,
                  top: 30,
                }}
                content={(() => {
                  const createdAt = node?.data?.createdAt
                    ? Utility.upperCaseFirstLetter(
                        dayjs(node.data.createdAt).format("MMM D, YYYY h:mm A"),
                      )
                    : t("ui.unknownDate");
                  const visibility = node?.data?.visibility || "private";
                  const sourceMap = {
                    job: t("ui.apiJob"),
                    widget: t("ui.web"),
                    private: t("ui.app"),
                    public: t("ui.app"),
                    incognito: t("ui.app"),
                  };
                  const source = sourceMap[visibility] || t("ui.app");
                  const readOnly =
                    visibility === "job" || visibility === "widget"
                      ? t("ui.readOnly")
                      : "";
                  return `${createdAt} • ${t(
                    "ui.source",
                  )}: ${source}${readOnly}`;
                })()}
                action="hover"
                position="right"
              >
                <TreeIcon
                  icon={getTreeIcon(treemode, node)}
                  color={getTreeIconColor(treemode, node)}
                />
              </Popable>
            ) : artifactExtensions.includes(node?.data?.type) ? (
              <TreeIcon icon={AppAssets.icons.image} />
            ) : dataTypes.includes(node?.data?.type) ? (
              <TreeIcon icon={AppAssets.icons[node?.data?.type]} />
            ) : (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={{
                  zIndex: 20,
                  width: 200,
                  top: 30,
                }}
                content={(() => {
                  const createdAt = node?.data?.createdAt
                    ? Utility.upperCaseFirstLetter(
                        dayjs(node.data.createdAt).format("MMM D, YYYY h:mm A"),
                      )
                    : t("ui.unknownDate");
                  const visibility = node?.data?.visibility || "private";
                  const sourceMap = {
                    job: t("ui.apiJob"),
                    widget: t("ui.web"),
                    private: t("ui.app"),
                    public: t("ui.app"),
                    incognito: t("ui.app"),
                  };
                  const source = sourceMap[visibility] || t("ui.app");
                  const readOnly =
                    visibility === "job" || visibility === "widget"
                      ? t("ui.readOnly")
                      : "";
                  return `${createdAt} • ${t(
                    "ui.source",
                  )}: ${source}${readOnly}`;
                })()}
                action="hover"
                position="right"
              >
                <TreeIcon
                  icon={
                    treemode == "CHATS"
                      ? AppAssets.icons.chatIcon
                      : AppAssets.icons.file
                  }
                />
              </Popable>
            )
          ) : (
            <Popable
              numberOfLines={2}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  zIndex: 20,

                  maxWidth: 300,
                },
              ]}
              content={getTreeTooltip(treemode, node)}
              action="hover"
              position="right"
            >
              <TreeIcon
                imageBorderRadius={4}
                imageSource={nodeImage}
                icon={getTreeIcon(treemode, node)}
                onPress={() => {
                  node.isInternal && node.toggle();
                }}
                color={getTreeIconColor(treemode, node)}
                isAgentIcon={node?.data?.is_agent}
                wrapperStyle={{
                  marginBottom: 3,
                }}
              />
            </Popable>
          )}
        </span>

        <span
          style={{
            color: node?.data?.is_favorite
              ? currentTheme?.theme.primary
              : currentTheme?.theme.darkGrey || "#666666",
            display: "inline-block",
            maxWidth: treemode == "CHATS" ? "200px" : "180px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
            fontSize: currentTheme?.theme.small_font_size,
            fontWeight:
              (treemode == "CHATS" &&
                (node?.data?.is_agent ||
                  node?.data?.is_agent_group ||
                  node?.data?.is_time_group)) ||
              (treemode != "CHATS" && node?.data?.droppable)
                ? "bold"
                : "normal",
          }}
          className="node_tree_tf"
        >
          {node.isEditing ? (
            <Input node={node} />
          ) : node.data.name === "New Chat" ? (
            t("chats.newChatName")
          ) : node?.data?.is_agent_group && node?.data?.groupEmoji ? (
            `${node.data.groupEmoji} ${node.data.name}`
          ) : (
            node.data.name
          )}
        </span>

        <span
          style={{
            color: currentTheme?.theme.primary,
            marginLeft: 2,
            fontSize: currentTheme?.theme.extra_small_font_size,
          }}
        >
          {node.data.children?.length > 0 &&
            `  (${node.data.children?.length})`}
        </span>
      </div>
      {allowParentAction &&
        node?.data?.is_agent &&
        node?.data?.is_agent_available != false && (
          <div
            style={{
              display: "inline-flex",
              float: "right",
              minWidth: isUserAIEngineer() ? "75px" : "50px",
            }}
          >
            <span
              style={{
                color: currentTheme?.theme.primary,
                position: "relative",
                marginRight: 5,
              }}
            >
              {!fixedNodeIds?.includes(node?.id) && (
                <div
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    display: "flex",
                    gap: 5,
                    opacity: node?.data?.is_favorite || isMouseOver ? 1 : 0,
                    pointerEvents:
                      node?.data?.is_favorite || isMouseOver ? "auto" : "none",
                    transition: "opacity 0.05s ease",
                  }}
                >
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 30,
                        zIndex: 20,
                        left: -15,
                        maxWidth: 300,
                      },
                    ]}
                    content={
                      node?.data?.is_favorite
                        ? t("ui.removeFromFavorites")
                        : t("ui.addToFavorites")
                    }
                    action="hover"
                    position="bottom"
                  >
                    <TreeIcon
                      wrapperStyle={{
                        marginBottom: 2,
                        zIndex: -1,
                      }}
                      icon={
                        node?.data?.is_favorite
                          ? AppAssets.icons.star
                          : AppAssets.icons.star
                      }
                      color={
                        node?.data?.is_favorite
                          ? currentTheme?.theme.primary
                          : currentTheme?.theme.font_color
                      }
                      onPress={() => {
                        onToggleFavorite(node?.id, !node?.data?.is_favorite);
                      }}
                    />
                  </Popable>
                  {isUserAIEngineer() && (
                    <Popable
                      numberOfLines={2}
                      backgroundColor={
                        currentTheme?.theme?.tooltipBackgroundColor
                      }
                      style={[
                        {
                          top: 30,
                          zIndex: 20,
                          left: -15,
                          maxWidth: 300,
                        },
                      ]}
                      content={t("ui.editAgent")}
                      action="hover"
                      position="bottom"
                    >
                      <TreeIcon
                        wrapperStyle={{
                          marginBottom: 2,
                        }}
                        icon={AppAssets.icons.edit}
                        color={currentTheme?.theme.font_color}
                        onPress={() => {
                          history.push(
                            `/workspaces/${
                              getActiveWorkspace()?.id
                            }/agents/${node?.id}`,
                          );
                        }}
                      />
                    </Popable>
                  )}
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 30,
                        left: -30,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("ui.startChat")}
                    action="hover"
                    position="bottom"
                  >
                    <TreeIcon
                      icon={AppAssets.icons.chatIcon}
                      onPress={() => {
                        onParentActionClick(node?.id);
                      }}
                    />
                  </Popable>
                </div>
              )}
            </span>
          </div>
        )}
      {showIconsOnSelection &&
        !isMultiDeleteMode &&
        node?.state?.isSelected &&
        (allowParentEdit || !node?.data?.droppable) && (
          <div
            style={{
              display: "inline-flex",
              float: "right",
              minWidth: "50px", // Reserve space for edit + delete icons
            }}
          >
            <span
              style={{
                color: currentTheme?.theme.primary,
                position: "relative",
                marginRight: 5,
              }}
            >
              {!fixedNodeIds?.includes(node?.id) && (
                <Popable
                  numberOfLines={2}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={t("ui.edit")}
                  action="hover"
                  position="bottom"
                >
                  <TreeIcon
                    icon={AppAssets.icons.edit}
                    wrapperStyle={{
                      opacity: isMouseOver ? 1 : 0,
                      pointerEvents: isMouseOver ? "auto" : "none",
                      transition: "opacity 0.05s ease",
                    }}
                    onPress={() => {
                      onSelectionChanged(node);
                      setDialogActionOnTree(
                        node?.data?.droppable
                          ? editLeafParentAction
                          : editLeafAction,
                      );
                    }}
                  />
                </Popable>
              )}
            </span>
            <span
              style={{
                color: currentTheme?.theme.red,
                position: "relative",
                marginRight: 5,
              }}
            >
              {!fixedNodeIds?.includes(node?.id) && (
                <Popable
                  numberOfLines={2}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      left: -12,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={t("ui.delete")}
                  action="hover"
                  position="bottom"
                >
                  <TreeIcon
                    icon={AppAssets.icons.delete}
                    color={currentTheme?.theme?.red}
                    wrapperStyle={{
                      opacity: isMouseOver ? 1 : 0,
                      pointerEvents: isMouseOver ? "auto" : "none",
                      transition: "opacity 0.05s ease",
                    }}
                    onPress={() => {
                      onSelectionChanged(node, true);
                      setDialogActionOnTree(deleteAction);
                    }}
                  />
                </Popable>
              )}
            </span>
          </div>
        )}
    </div>
  );
}

function Input({ node }) {
  return (
    <input
      autoFocus
      type="text"
      defaultValue={node.data.name}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={() => node.reset()}
      onKeyDown={(e) => {
        if (e.key === "Escape") node.reset();
        if (e.key === "Enter") node.submit(e.currentTarget.value);
      }}
    />
  );
}

function Row({ node, innerRef, attrs, children }) {
  return <AppText> {node?.data?.name || ""}</AppText>;
}

function FolderArrow({ node, onPress }) {
  const { currentTheme } = AppTheme.useTheme();
  if (!node?.data?.droppable) return <span></span>;
  return (
    <span
      style={{
        position: "relative",
        marginRight: 5,
        color: currentTheme?.theme.primary,
      }}
    >
      {node.isOpen ? (
        <TreeIcon icon={AppAssets.icons.arrow_down} onPress={onPress} />
      ) : (
        <TreeIcon icon={AppAssets.icons.arrow_right} onPress={onPress} />
      )}
    </span>
  );
}
const layerStyles = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 100,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};
const getStyle = (offset) => {
  if (!offset) return { display: "none" };
  const { x, y } = offset;
  return { transform: `translate(${x}px, ${y}px)` };
};
const Overlay = memo(function Overlay(props) {
  if (!props.isDragging) return null;
  return <div style={layerStyles}>{props.children}</div>;
});

function Position(props) {
  return (
    <div className="row preview" style={getStyle(props.offset)}>
      {props.children}
    </div>
  );
}
const PreviewNode = memo(function PreviewNode(props) {
  const { treeData, findNodeById, treemode } = useTreeview();
  const { currentTheme } = AppTheme.useTheme();
  const node = findNodeById(props.id, treeData)[0];
  const count = Array.isArray(props.dragIds) ? props.dragIds.length : 1;
  return (
    <div style={{ position: "relative" }}>
      <CoreNodeView
        treemode={treemode}
        isDrag={true}
        droppable={node?.droppable}
        name={node?.name}
        currentTheme={currentTheme}
        description={node?.description}
      />
      {count > 1 && (
        <div
          style={{
            position: "absolute",
            top: -8,
            right: -8,
            minWidth: 20,
            height: 20,
            paddingHorizontal: 5,
            borderRadius: 10,
            backgroundColor: currentTheme?.theme?.primary,
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        >
          <AppText
            color={currentTheme?.theme?.white}
            style={{ fontSize: 11, lineHeight: 14 }}
            fontWeight="bold"
          >
            {count}
          </AppText>
        </div>
      )}
    </div>
  );
});
function DragPreview({ offset, mouse, id, dragIds, isDragging }) {
  const correctedOffset = {
    x: (mouse?.x || offset?.x || 0) - 60,
    y: (mouse?.y || offset?.y || 0) - 20,
  };
  return (
    <Overlay isDragging={isDragging}>
      <Position offset={correctedOffset}>
        <PreviewNode id={id} dragIds={dragIds} />
      </Position>
    </Overlay>
  );
}

export default {
  Node,
  Input,
  FolderArrow,
  Row,
  DragPreview,
};
